import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { get, post } from './api';
import { t, LanguageSwitcher } from '@omnifuse/shared/frontend/i18n';
import { SettingsPanel } from '@omnifuse/shared/frontend/SettingsPanel';
import { get as apiGet, post as apiPost, put as apiPut } from './api';

const S = {
  page: { maxWidth: 1100, margin: '0 auto', padding: 24 } as React.CSSProperties,
  card: { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,.08)', marginBottom: 16 } as React.CSSProperties,
  btn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  btnPrimary: { padding: '6px 14px', borderRadius: 8, border: 'none', background: '#1a73e8', color: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { textAlign: 'left' as const, padding: '8px 10px', borderBottom: '2px solid #eee', color: '#555' },
  td: { padding: '8px 10px', borderBottom: '1px solid #f0f0f0' },
  tab: (a: boolean) => ({ padding: '8px 18px', cursor: 'pointer', borderBottom: a ? '2px solid #1a73e8' : '2px solid transparent', color: a ? '#1a73e8' : '#555', fontWeight: a ? 600 : 400, fontSize: 14 }) as React.CSSProperties,
  input: { padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 } as React.CSSProperties,
  chip: { display: 'inline-block', margin: '0 6px 6px 0', padding: '2px 10px', borderRadius: 12, fontSize: 12, background: '#e8f0fe' } as React.CSSProperties,
};

function AssetsView() {
  const [rows, setRows] = useState<any[]>([]);
  const [form, setForm] = useState({ source_url: '', title: '', asset_type: 'image', creator_handle: '' });
  const [detail, setDetail] = useState<any>(null);
  const [busy, setBusy] = useState('');

  const load = () => get('/api/v1/assets').then((r: any) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.source_url) return alert('请填写素材 URL');
    try { await post('/api/v1/assets', form); setForm({ ...form, source_url: '', title: '' }); load(); }
    catch (e: any) { alert(e.message); }
  };

  const analyze = async (id: string) => {
    setBusy(id);
    try { await post(`/api/v1/assets/${id}/analyze`); await openDetail(id); load(); }
    catch (e: any) { alert(e.message); }
    setBusy('');
  };

  const genCaptions = async (id: string) => {
    setBusy(id);
    try { await post(`/api/v1/assets/${id}/captions`); await openDetail(id); }
    catch (e: any) { alert(e.message); }
    setBusy('');
  };

  const openDetail = async (id: string) => {
    try {
      const [h, cap]: any[] = await Promise.all([
        get(`/api/v1/assets/${id}/highlights`),
        get(`/api/v1/assets/${id}/captions`),
      ]);
      setDetail({ id, highlights: h.data || [], captions: cap.data || [] });
    } catch { /* ignore */ }
  };

  return (
    <>
      <div style={S.card}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={{ ...S.input, flex: 1 }} placeholder={t('素材 URL（图片/视频封面）')} value={form.source_url} onChange={e => setForm({ ...form, source_url: e.target.value })} />
          <input style={{ ...S.input, width: 160 }} placeholder={t('标题')} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
          <select style={S.input} value={form.asset_type} onChange={e => setForm({ ...form, asset_type: e.target.value })}>
            <option value="image">{t('图片')}</option><option value="video">{t('视频')}</option>
          </select>
          <input style={{ ...S.input, width: 130 }} placeholder={t('创作者 @')} value={form.creator_handle} onChange={e => setForm({ ...form, creator_handle: e.target.value })} />
          <button style={S.btnPrimary} onClick={add}>{t('＋ 添加素材')}</button>
        </div>
      </div>
      {detail && (
        <div style={{ ...S.card, background: '#f8f9fa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <strong>{t('素材详情')}</strong>
            <button style={S.btn} onClick={() => setDetail(null)}>{t('关闭')}</button>
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>🎬 高光画面（{detail.highlights.length}）</div>
          {detail.highlights.map((h: any) => (
            <div key={h.id} style={{ borderLeft: '3px solid #1a73e8', paddingLeft: 10, marginBottom: 8, fontSize: 12 }}>
              <span style={{ fontWeight: 600 }}>[{h.suggested_use}] hook {Math.round(h.hook_score)}</span>
              {h.time_start !== null && ` · ${h.time_start}s-${h.time_end}s`} — {h.description}
            </div>
          ))}
          <div style={{ fontSize: 12, fontWeight: 600, margin: '10px 0 4px' }}>{t('📝 多平台文案')}</div>
          {detail.captions.map((cp: any) => (
            <div key={cp.id} style={{ marginBottom: 8, fontSize: 12 }}>
              <strong>{cp.platform}</strong>: {cp.caption}
              <div>{JSON.parse(cp.hashtags_json || '[]').map((t: string) => <span key={t} style={S.chip}>#{t}</span>)}</div>
            </div>
          ))}
        </div>
      )}
      <div style={S.card}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('素材')}</th><th style={S.th}>{t('类型')}</th><th style={S.th}>{t('创作者')}</th><th style={S.th}>{t('状态')}</th><th style={S.th}>{t('操作')}</th></tr></thead>
          <tbody>
            {rows.map((r: any) => (
              <tr key={r.id}>
                <td style={{ ...S.td, cursor: 'pointer', color: '#1a73e8' }} onClick={() => openDetail(r.id)}>{r.title || r.source_url.slice(0, 50)}</td>
                <td style={S.td}>{r.asset_type}</td>
                <td style={S.td}>{r.creator_handle || '-'}</td>
                <td style={S.td}>{r.status}</td>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                  <span style={{ display: 'inline-flex', gap: 6 }}>
                    <button style={S.btn} disabled={busy === r.id} onClick={() => analyze(r.id)}>{busy === r.id ? '...' : t('🔍 高光分析')}</button>
                    {r.status !== 'new' && <button style={S.btn} disabled={busy === r.id} onClick={() => genCaptions(r.id)}>{t('📝 生成文案')}</button>}
                  </span>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td style={S.td} colSpan={5}>{t('暂无素材')}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

function FatigueView() {
  const [rows, setRows] = useState<any[]>([]);

  const load = () => get('/api/v1/fatigue').then((r: any) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const record = async (id: string) => {
    const imp = prompt('近期曝光数？');
    if (imp === null) return;
    const clk = prompt(t('近期点击数？'), '0');
    try {
      const r: any = await post(`/api/v1/assets/${id}/fatigue`, { impressions: Number(imp), clicks: Number(clk || 0) });
      alert(`CTR ${r.data.ctr}% · 疲劳分 ${r.data.fatigue_score}\n${r.data.recommendation}`);
      load();
    } catch (e: any) { alert(e.message); }
  };

  return (
    <div style={S.card}>
      <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('素材疲劳监控（CTR 衰减检测）')}</h3>
      <table style={S.table}>
        <thead><tr><th style={S.th}>{t('素材')}</th><th style={S.th}>{t('状态')}</th><th style={S.th}>{t('疲劳分')}</th><th style={S.th}>{t('建议')}</th><th style={S.th}></th></tr></thead>
        <tbody>
          {rows.map((r: any) => (
            <tr key={r.id}>
              <td style={S.td}>{r.title || r.id}</td>
              <td style={S.td}>{r.status}</td>
              <td style={{ ...S.td, fontWeight: 700, color: (r.latest_fatigue ?? 0) >= 60 ? '#d93025' : (r.latest_fatigue ?? 0) >= 35 ? '#e8710a' : '#188038' }}>
                {r.latest_fatigue ?? '-'}
              </td>
              <td style={{ ...S.td, fontSize: 12, maxWidth: 320 }}>{r.recommendation || '-'}</td>
              <td style={S.td}><button style={S.btn} onClick={() => record(r.id)}>{t('录入投放数据')}</button></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={S.td} colSpan={5}>{t('暂无已分析素材')}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function App() {
  const [tab, setTab] = useState('assets');
  return (
    <div style={S.page}>
      <div style={{ position: 'fixed', top: 12, right: 16, zIndex: 1000 }}><LanguageSwitcher /></div>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>UGCStudioAI</h1>
      <p style={{ color: '#666', marginBottom: 16 }}>{t('UGC 视频提炼与素材转化 — 高光提取 · 多平台文案 · 疲劳检测')}</p>
      <div style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: 20 }}>
        <div style={S.tab(tab === 'assets')} onClick={() => setTab('assets')}>{t('素材库')}</div>
        <div style={S.tab(tab === 'fatigue')} onClick={() => setTab('fatigue')}>{t('疲劳监控')}</div>
        <div style={S.tab(tab === 'settings')} onClick={() => setTab('settings')}>{t('设置')}</div>
      </div>
      {tab === 'assets' && <AssetsView />}
      {tab === 'fatigue' && <FatigueView />}
      {tab === 'settings' && <SettingsPanel api={{ get: apiGet, post: apiPost, put: apiPut }} />}
    </div>
  );
}

const root = createRoot(document.getElementById('root')!);
root.render(<App />);
